import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { CalendarIcon, EditIcon, MailIcon, ShieldIcon } from "lucide-react";
import { NameAvatar } from "@/components/name-avatar";
import { useAuthStore } from "@/stores/authStore";
import { useTranslation } from "react-i18next";
import { EditProfileDialog } from "./edit-profile-dialog";
import { ChangePasswordDialog } from "./change-password-dialog";
import { Main } from "@/components/layout/main";
import { UserAssignedCasesTable } from "@/views/users/page/UserAssignedCasesTable";
import { UserRendezvousTable } from "@/views/users/page/UserRendezvousTable";
import { Case } from "../cases/data/schema";

export default function ProfilePage() {
  const { user } = useAuthStore().auth;
  const { t } = useTranslation();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
  const [assignedCases, setAssignedCases] = useState<Case[]>([]);
  const [loadingCases, setLoadingCases] = useState(false);

  if (!user) {
    return <div>{t('loadingUserData')}</div>;
  }

  return (
    <>
      <Main>
        <div className="mb-6">
          <h1 className="text-3xl font-bold">{t('profilePageTitle')}</h1>
        </div>

        <div className="grid gap-6">
          <Card>
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle>{t('accountInfoCardTitle')}</CardTitle>
                <Button variant="outline" size="icon" onClick={() => setIsEditModalOpen(true)}>
                  <EditIcon className="h-4 w-4" />
                </Button>
              </div>
              <CardDescription>{t('accountInfoCardDescription')}</CardDescription>
            </CardHeader>

            <CardContent>
              <div className="flex flex-col md:flex-row gap-6">
                <div className="flex flex-col items-center gap-3">
                  <NameAvatar name={user.name} className="h-32 w-32 text-4xl" />

                  <Badge className="gap-1">
                    <span>{user.role}</span>
                  </Badge>
                  {user.is_admin && (
                    <Badge className="gap-1">
                      <ShieldIcon className="h-3.5 w-3.5" />
                      <span>{t('adminBadgeText')}</span>
                    </Badge>
                  )}
                </div>

                <div className="flex-1 space-y-4">
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground">{t('nameLabel')}</h3>
                    <p className="text-lg font-medium">{user.name}</p>
                  </div>

                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground">{t('usernameLabel')}</h3>
                    <p className="text-lg">{user.username}</p>
                  </div>

                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground">{t('emailLabel')}</h3>
                    {user.email ? (
                      <div className="flex items-center gap-2">
                        <MailIcon className="h-4 w-4 text-muted-foreground" />
                        <p>{user.email}</p>
                      </div>
                    ) : (
                      <div>
                        <p className="text-sm text-muted-foreground">{t('notSet')}</p>
                        <p className="text-xs text-red-500 mt-1">{t('addEmailWarning')}</p>
                      </div>
                    )}
                  </div>

                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground">{t('creationDateLabel')}</h3>
                    <div className="flex items-center gap-2">
                      <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                      <p>{new Date(user.created_at * 1000).toLocaleDateString('en-GB')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>

            <Separator />

            <CardFooter className="flex justify-between pt-6">
              <div className="flex items-center text-sm text-muted-foreground">
                <ShieldIcon className="h-4 w-4 ml-1" />
                {user.is_admin ? t('adminAccountStatus') : t('userAccountStatus')}
              </div>
              <Button variant="outline" onClick={() => setIsChangePasswordModalOpen(true)}>
                {t('changePasswordButton')}
              </Button>
            </CardFooter>
          </Card>

          {useAuthStore.getState().auth.user?.calendar_access === null && (
            <UserAssignedCasesTable
              userId={user.id}
              loadingCases={loadingCases}
              setLoadingCases={setLoadingCases}
              assignedCases={assignedCases}
              setAssignedCases={setAssignedCases}
            />
          )}
          <UserRendezvousTable userId={user.id} />
        </div>
      </Main>

      <EditProfileDialog
        open={isEditModalOpen}
        onOpenChange={setIsEditModalOpen}
        isProfilePage={true}
      />

      <ChangePasswordDialog
        open={isChangePasswordModalOpen}
        onOpenChange={setIsChangePasswordModalOpen}
      />
    </>
  );
}